import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CloseIcon } from "public/static/icons";
import Logo from "public/static/logo.svg";
import Image from "next/image";

interface IProps {
  show: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}

export function Modal({ show, onClose, children, className }: IProps) {
  const [isBrowser, setIsBrowser] = useState(false);

  useEffect(() => {
    setIsBrowser(true);
  }, []);

  const handleCloseClick = (e) => {
    e.preventDefault();
    onClose();
  };

  useEffect(() => {
    if (show) {
      document.body.classList.add("active-modal");
    } else {
      document.body.classList.remove("active-modal");
    }
  }, [show]);

  if (isBrowser) {
    return ReactDOM.createPortal(
      <AnimatePresence>
        {show ? (
          <StyledModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <StyledModal
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <StyledMobileHeader>
                <StyledImage src={Logo} alt="logo" />
                <button type="button" onClick={handleCloseClick}>
                  <CloseIcon />
                </button>
              </StyledMobileHeader>
              <StyledContent className={className}>{children}</StyledContent>
            </StyledModal>
          </StyledModalOverlay>
        ) : null}
      </AnimatePresence>,
      document.getElementById("modal-root")
    );
  }
  return null;
}

const StyledModal = styled(motion.div)`
  background: white;
  border-radius: 1.2rem;
  padding: 5.6rem 3.2rem 3.2rem 3.2rem;

  @media screen and (max-width: ${(props) => props.theme.sizes.tablet}) {
    border-radius: unset;
    padding: 0 1.6rem 3.2rem;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 3;
    display: grid;
    grid-template-rows: auto 1fr;
    overflow: auto;
  }
`;

const StyledModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const StyledMobileHeader = styled.div`
  display: none;
  @media screen and (max-width: ${(props) => props.theme.sizes.tablet}) {
    display: grid;
    grid-auto-flow: column;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0.8rem 0;
  }
`;

const StyledImage = styled(Image)`
  max-height: 4rem;
  width: auto;
`;

const StyledContent = styled.div`
  @media screen and (max-width: ${(props) => props.theme.sizes.tablet}) {
    display: grid;
    align-content: center;
    justify-items: center;
  }
`;
